import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const { isDarkMode, colorScheme, colorSchemes } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];
    
    // Developer-themed colors for binary/hex particles
    const darkModeColor = '#00FF41'; // Matrix green
    const lightModeColor = '#64748B'; // Dark gray/blue
    
    // Binary and hex character sets
    const binaryChars = ['0', '1'];
    const hexPrefixes = ['0x'];
    const hexDigits = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'A', 'B', 'C', 'D', 'E', 'F'];

    const resizeCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    const createParticle = () => {
      // Generate text content (60% binary, 40% hex)
      let text;
      if (Math.random() < 0.6) {
        // Binary digit
        text = binaryChars[Math.floor(Math.random() * binaryChars.length)];
      } else {
        // Hex code
        const prefix = hexPrefixes[0];
        const digitCount = Math.floor(Math.random() * 3) + 2; // 2-4 digits
        let hexValue = '';
        for (let i = 0; i < digitCount; i++) {
          hexValue += hexDigits[Math.floor(Math.random() * hexDigits.length)];
        }
        text = prefix + hexValue;
      }
      
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        text: text,
        fontSize: Math.random() * 4 + 14, // 14px - 18px
        opacity: 0,
        maxOpacity: Math.random() * 0.6 + 0.8,
        life: Math.random() * 500 + 300,
        fadeIn: Math.random() * 100 + 50,
        fadeOut: Math.random() * 100 + 50
      };
    };

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < 150; i++) {
        particles.push(createParticle());
      }
    };

    const updateParticles = () => {
      particles.forEach((particle, index) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.life--;

        // Fade in/out animation (slower)
        if (particle.life > particle.fadeOut) {
          // Fade in
          particle.opacity = Math.min(particle.maxOpacity, particle.opacity + 0.01);
        } else {
          // Fade out
          particle.opacity = Math.max(0, particle.opacity - 0.01);
        }

        if (particle.life <= 0 || particle.x < 0 || particle.x > canvas.width || 
            particle.y < 0 || particle.y > canvas.height) {
          particles[index] = createParticle();
        }
      });
    };

    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Enable font smoothing for crisp text
      ctx.imageSmoothingEnabled = false;
      
      // Set font properties for all particles
      ctx.font = 'normal 16px "Courier New", "Consolas", monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      particles.forEach(particle => {
        ctx.save();
        ctx.globalAlpha = particle.opacity;
        
        // Set color based on theme
        ctx.fillStyle = isDarkMode ? darkModeColor : lightModeColor;
        
        // Set individual font size for this particle
        ctx.font = `normal ${particle.fontSize}px "Courier New", "Consolas", monospace`;
        
        // Draw the text
        ctx.fillText(particle.text, particle.x, particle.y);
        ctx.restore();
      });

      // Draw connections between nearby particles (optional - can be removed for cleaner look)
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 100) {
            ctx.save();
            ctx.globalAlpha = (100 - distance) / 100 * 0.05; // Very subtle connections
            ctx.strokeStyle = isDarkMode ? darkModeColor : lightModeColor;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
            ctx.restore();
          }
        });
      });
    };

    const animate = () => {
      updateParticles();
      drawParticles();
      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    initParticles();
    animate();

    const handleResize = () => {
      resizeCanvas();
      initParticles();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [isDarkMode, colorScheme, colorSchemes]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none opacity-90"
      style={{ zIndex: 0 }}
    />
  );
};

export default ParticleBackground;
